import React from "react";
import { useDrag } from "react-dnd";
import { Popover } from "antd";
import histo from "../assets/histo.png";
import macro from "../assets/macro.jpg";
import radio from "../assets/radio.png";
import { GiMicroscope, GiMagnifyingGlass } from "react-icons/gi";
import { LiaXRaySolid } from "react-icons/lia";
import { UpOutlined, DownOutlined } from "@ant-design/icons";

export const EXAM_BAR_EXPANDED_HEIGHT = 176;
export const EXAM_BAR_COLLAPSED_HEIGHT = 40;

// Per-modality presentation. Fallback static images are used only when an asset
// has no generated thumbnail yet.
const MODALITY_META = {
  macro: { title: "Macroscopique", icon: <GiMagnifyingGlass />, fallback: macro },
  radio: { title: "Radiologie", icon: <LiaXRaySolid />, fallback: radio },
  histo: { title: "Histologie", icon: <GiMicroscope />, fallback: histo },
};
const MODALITY_ORDER = ["macro", "radio", "histo"];

// Build the modality categories + drag items from the case's API images so the
// bar is per-case and each vignette is the real backend thumbnail.
const buildCategories = (images = []) => {
  const byModality = {};
  images.forEach((img) => {
    const meta = MODALITY_META[img.modality];
    if (!meta) return;
    (byModality[img.modality] ||= []).push({
      id: img.id,
      type: img.modality,
      content: {
        url: img.url,
        name: img.name,
        assetId: img.id,
        modality: img.modality,
        thumbnailUrl: img.thumbnailUrl,
      },
      preview: img.thumbnailUrl || meta.fallback,
      title: img.name || meta.title,
    });
  });
  return MODALITY_ORDER.filter((m) => byModality[m]).map((m) => ({
    id: m,
    title: MODALITY_META[m].title,
    icon: MODALITY_META[m].icon,
    exams: byModality[m],
  }));
};

const ExamThumb = ({ exam }) => {
  const [{ isDragging }, drag] = useDrag({
    type: "EXAM",
    item: exam,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });
  const [hovered, setHovered] = React.useState(false);

  return (
    <div
      ref={drag}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        cursor: "grab",
        opacity: isDragging ? 0.4 : 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 4,
        transform: hovered && !isDragging ? "translateY(-2px)" : "none",
        transition: "transform 0.15s ease, opacity 0.15s ease",
      }}
    >
      <div
        style={{
          width: 108,
          height: 80,
          borderRadius: "var(--radius-md)",
          overflow: "hidden",
          border: `1px solid ${
            hovered ? "var(--color-primary)" : "var(--color-border)"
          }`,
          background: "var(--color-surface)",
          boxShadow: hovered ? "var(--shadow-md)" : "var(--shadow-sm)",
          transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        }}
      >
        <img
          src={exam.preview}
          alt={exam.title}
          draggable={false}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      </div>
      <span
        style={{
          fontSize: 11,
          color: "var(--color-text-secondary)",
          maxWidth: 112,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={exam.title}
      >
        {exam.title}
      </span>
    </div>
  );
};

const CategoryGroup = ({ category, isLast }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      gap: 6,
      paddingRight: isLast ? 0 : 20,
      marginRight: isLast ? 0 : 4,
      borderRight: isLast ? "none" : "1px solid var(--color-border-subtle)",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        color: "var(--color-text-secondary)",
        fontSize: 11,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: 0.4,
      }}
    >
      {React.cloneElement(category.icon, { size: 14 })}
      <span>{category.title}</span>
    </div>
    <div style={{ display: "flex", gap: 12 }}>
      {category.exams.map((exam) => (
        <ExamThumb key={exam.id} exam={exam} />
      ))}
    </div>
  </div>
);

const CollapsedCategoryChip = ({ category }) => {
  const [hovered, setHovered] = React.useState(false);
  const content = (
    <div style={{ display: "flex", gap: 12, padding: 4 }}>
      {category.exams.map((exam) => (
        <ExamThumb key={exam.id} exam={exam} />
      ))}
    </div>
  );

  return (
    <Popover
      content={content}
      title={category.title}
      placement="top"
      trigger="hover"
    >
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "4px 10px",
          borderRadius: 999,
          background: hovered ? "var(--color-surface)" : "transparent",
          border: "1px solid var(--color-border)",
          color: "var(--color-text)",
          fontSize: 12,
          cursor: "pointer",
          transition: "background 0.15s ease",
        }}
      >
        {React.cloneElement(category.icon, { size: 14 })}
        <span>{category.title}</span>
      </div>
    </Popover>
  );
};

const ExamSidebar = ({ isCollapsed, onToggle, images = [] }) => {
  const height = isCollapsed
    ? EXAM_BAR_COLLAPSED_HEIGHT
    : EXAM_BAR_EXPANDED_HEIGHT;

  const categories = React.useMemo(() => buildCategories(images), [images]);

  return (
    <div
      style={{
        width: "100%",
        height,
        flexShrink: 0,
        background: "var(--color-sidebar-bg)",
        borderTop: "1px solid var(--color-border)",
        color: "var(--color-text)",
        display: "flex",
        flexDirection: "column",
        transition: "height 0.25s ease",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          height: EXAM_BAR_COLLAPSED_HEIGHT,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 16px",
          borderBottom: isCollapsed
            ? "none"
            : "1px solid var(--color-border-subtle)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "var(--color-text-secondary)",
              textTransform: "uppercase",
              letterSpacing: 0.6,
            }}
          >
            Modalités
          </span>
          {isCollapsed && (
            <div style={{ display: "flex", gap: 8 }}>
              {categories.map((category) => (
                <CollapsedCategoryChip
                  key={category.id}
                  category={category}
                />
              ))}
            </div>
          )}
        </div>
        <button
          onClick={() => onToggle(!isCollapsed)}
          aria-label={isCollapsed ? "Déplier" : "Replier"}
          style={{
            width: 28,
            height: 28,
            borderRadius: "var(--radius-sm)",
            border: "1px solid var(--color-border)",
            background: "var(--color-bg)",
            color: "var(--color-text-secondary)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background 0.15s ease",
          }}
        >
          {isCollapsed ? <UpOutlined /> : <DownOutlined />}
        </button>
      </div>

      {!isCollapsed && (
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: 4,
            padding: "8px 16px",
            overflowX: "auto",
            overflowY: "hidden",
          }}
        >
          {categories.map((category, idx) => (
            <CategoryGroup
              key={category.id}
              category={category}
              isLast={idx === categories.length - 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export { ExamSidebar };
